import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Rocket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/app/AppShell";

const STEPS = [
  { n: 1,  title: "Welcome",              body: "A prospect submits our Jotform intake with basic info, package selection, and desired funding goals.", accent: "from-amber-400 to-amber-600" },
  { n: 2,  title: "Account created",      body: "The moment the form submits, a ClientQuack portal is auto-created with the client's email as username and a deterministic temp password (LastName + last 4 of phone).", accent: "from-amber-400 to-orange-500" },
  { n: 3,  title: "Documents uploaded",   body: "Photo ID, SS card, utility bill, pay stubs. Drag-and-drop into the portal, AES-256 encrypted at rest.", accent: "from-orange-400 to-red-500" },
  { n: 4,  title: "AI Credit Analysis",   body: "Claude Sonnet reads the client's file and produces a personalized readiness score, negative-item map, and dispute strategy.", accent: "from-red-400 to-pink-500" },
  { n: 5,  title: "Dispute Generation",   body: "Lorenzo and the credit team draft dispute letters using the AI-generated strategy plus their manual expertise. Sent to Experian, Equifax, TransUnion.", accent: "from-pink-400 to-fuchsia-500" },
  { n: 6,  title: "Tracking progress",    body: "Client watches the Pizza Tracker move in real time. Every status change (Started → Waiting for Info → Doc Issue → Waiting for Repairs → Waiting on Trade Line → Waiting on Funding → Completed) is instant.", accent: "from-fuchsia-400 to-violet-500" },
  { n: 7,  title: "Funding marketplace",  body: "As the score climbs, the Funding Center starts recommending eligible programs — personal cards first, then business credit, then higher-limit lines.", accent: "from-violet-400 to-indigo-500" },
  { n: 8,  title: "Business credit build", body: "For clients pursuing business capital, we set up EINs, DUNS, tradelines, and prep for SBA / working capital.", accent: "from-indigo-400 to-blue-500" },
  { n: 9,  title: "Client dashboard",     body: "Every touchpoint — messages, documents, meetings, status, funding readiness — lives in a single dashboard. Nothing off-platform.", accent: "from-blue-400 to-sky-500" },
  { n: 10, title: "Monitoring",           body: "Continuous credit monitoring via IdentityIQ integration. Alerts on new inquiries, score changes, and fraud attempts.", accent: "from-sky-400 to-cyan-500" },
  { n: 11, title: "Funded",               body: "The client applies for the recommended programs with confidence. Approval screenshots come back to us and go into the wall of proof.", accent: "from-cyan-400 to-emerald-500" },
];

export default function HowItWorks() {
  const { user } = useAuth();
  const body = (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
      {!user && (
        <header className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/docs" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="hiw-back">
              <ArrowLeft size={15} /> Help Center
            </Link>
            <p className="text-xs text-slate-400 font-semibold hidden sm:block">11-step guided walkthrough</p>
          </div>
        </header>
      )}
      {user && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <Link to="/docs" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="hiw-back">
            <ArrowLeft size={15} /> Help Center
          </Link>
        </div>
      )}

      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-amber-200/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-sky-200/40 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.28em] font-bold text-amber-600">
            <Rocket size={13} /> How Credit Quack works
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-black text-slate-900 mt-4 leading-tight">
            From <span className="cq-text-gradient-gold">first intake</span><br />to funded — in 11 steps.
          </h1>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            An immersive walkthrough of exactly how a client moves through Credit Quack. No black boxes, no vague timelines — every stage documented.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative pl-8 sm:pl-12">
          {/* vertical rail */}
          <div className="absolute left-3 sm:left-5 top-4 bottom-4 w-px bg-gradient-to-b from-amber-300 via-slate-200 to-emerald-300" />

          {STEPS.map((s, i) => (
            <div key={s.n} className="relative pb-14 last:pb-0 cq-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              {/* Node */}
              <div className={`absolute -left-8 sm:-left-12 top-1 h-7 w-7 rounded-full bg-gradient-to-br ${s.accent} shadow-lg shadow-slate-900/10 flex items-center justify-center text-white text-xs font-black`}>
                {s.n}
              </div>
              <div className="bg-white border border-slate-100 rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition" data-testid={`hiw-step-${s.n}`}>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Step {s.n}</p>
                <h2 className="font-display text-2xl sm:text-3xl font-black text-slate-900 mt-1">{s.title}</h2>
                <p className="text-slate-700 mt-3 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}

          {/* Completion badge */}
          <div className="relative">
            <div className="absolute -left-8 sm:-left-12 top-1 h-7 w-7 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30 flex items-center justify-center text-white">
              <CheckCircle2 size={16} />
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-3xl p-7">
              <p className="text-xs uppercase tracking-widest font-bold text-emerald-700">Complete</p>
              <h2 className="font-display text-3xl font-black text-slate-900 mt-1">You're funded and organized for the next round.</h2>
              <p className="text-slate-700 mt-2">Most Credit Quack clients cycle through steps 3–7 in 30–90 days. Every account is different — that's why every file has a real specialist assigned from day one.</p>
              <Link to="/#signup" className="inline-flex items-center gap-2 mt-5 bg-slate-900 hover:bg-slate-800 text-white h-12 px-6 rounded-2xl font-black transition" data-testid="hiw-signup">
                Start my file <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  return user ? <AppShell>{body}</AppShell> : body;
}
